import {
  GAMES_PLAYED_CHOICES,
  LANGUAGE_CHOICES,
} from "../../constants/tenderChoices";
import { formatGroupedNumber } from "../../utils/currencyFormatter";
import CustomDropdown from "../common/CustomDropdown";
import { useUiLanguage } from "../profile/coaching-personality/useUiLanguage";
import i18n from "../../i18n";
import { getStaticTranslation } from "../../translations/registerStaticTranslations";

// fieldErrors (optional): inline validation errors keyed by error group —
// this step renders the "position" and "market_value" groups (T004).
const TenderFormRequirements = ({
  formData,
  setFormData,
  choices,
  fieldErrors = {},
}) => {
  // QAV5-502: window.alert() is a native browser dialog the DOM scanner
  // cannot reach — route it through the same static-bundle lookup the
  // scanner uses before it goes to the native dialog.
  const currentLanguage = useUiLanguage();
  const tr = (s) => getStaticTranslation(i18n, s, currentLanguage) || s;
  const {
    playerPositions,
    staffPositions,
    footChoices,
    marketValueTypes,
    experienceChoices,
  } = choices;

  // Input mask for the market-value fields — same rule as the fee mask in
  // TenderFormDetails: apostrophe grouping (Marin, 2026-08-02), and the strip
  // accepts a comma too so a value written before the change still reopens
  // readably.
  const formatAmount = (value) => {
    if (!value) return "";
    return formatGroupedNumber(String(value).replace(/[,']/g, ""), {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  const handleAmountChange = (e, field, isRange = false, rangeField = null) => {
    let value = e.target.value.replace(/[^0-9]/g, "");
    // Limit to 9 digits maximum (up to 999,999,999)
    if (value.length > 9) {
      value = value.substring(0, 9);
    }
    if (isRange) {
      setFormData({
        ...formData,
        marketValueRange: { ...formData.marketValueRange, [rangeField]: value },
      });
    } else {
      setFormData({ ...formData, [field]: value });
    }
  };

  const handleLanguageChange = (value) => {
    if (value && !formData.languageSkills.includes(value)) {
      setFormData((prev) => ({
        ...prev,
        languageSkills: [...prev.languageSkills, value],
      }));
    }
  };

  const removeLanguage = (lang) => {
    setFormData((prev) => ({
      ...prev,
      languageSkills: prev.languageSkills.filter((l) => l !== lang),
    }));
  };

  const openUrlInNewTab = (url) => {
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const openBlobDocument = (blob, name = "document") => {
    if (!blob) return;
    const blobUrl = URL.createObjectURL(blob);

    if (blob.type && blob.type.startsWith("image/")) {
      openUrlInNewTab(blobUrl);
    } else if (blob.type === "application/pdf") {
      const pdfWindow = window.open("", "_blank", "width=800,height=600,scrollbars=yes,resizable=yes");
      if (pdfWindow && pdfWindow.document) {
        pdfWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>${name} - PDF Viewer</title>
              <style>
                body { margin: 0; padding: 0; height: 100vh; }
                embed {
                  width: 100%;
                  height: 100%;
                  border: none;
                }
              </style>
            </head>
            <body>
              <embed src="${blobUrl}#toolbar=1&navpanes=1&scrollbar=1" type="application/pdf">
            </body>
          </html>
        `);
      }
    } else {
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
  };

  const handleViewDocument = (documentItem) => {
    if (!documentItem) return;

    if (typeof documentItem === "string") {
      openUrlInNewTab(documentItem);
      return;
    }

    if (documentItem.url || documentItem.document_url || documentItem.path || documentItem.file) {
      const directUrl =
        documentItem.url ||
        documentItem.document_url ||
        documentItem.path ||
        (typeof documentItem.file === "string" ? documentItem.file : null);

      if (directUrl) {
        openUrlInNewTab(directUrl);
        return;
      }
    }

    if (documentItem instanceof Blob) {
      openBlobDocument(documentItem, documentItem.name || "document");
      return;
    }

    if (documentItem.file instanceof Blob) {
      openBlobDocument(documentItem.file, documentItem.name || "document");
    }
  };

  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    const iconClass = "w-5 h-5";

    switch (extension) {
      case 'pdf':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />;
      case 'doc':
      case 'docx':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />;
      case 'txt':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />;
      default:
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (typeof bytes !== "number" || Number.isNaN(bytes) || bytes <= 0) {
      return null;
    }
    const sizeInMb = bytes / (1024 * 1024);
    return `${sizeInMb.toFixed(2)} MB`;
  };

  const hasNewDocument = Array.isArray(formData.documents) && formData.documents.length > 0;
  const shouldShowExistingDocument =
    !hasNewDocument && formData.existingDocument && !formData.documentMarkedForRemoval;

  return (
    <div className="bg-[#F8F9FB] rounded-lg p-8 mt-6">
      <h3 className="text-xs font-medium uppercase text-secondary mb-6">
        REQUIREMENTS
      </h3>

      <div className="space-y-6">
        <div className="grid sm:grid-cols-2 gap-6">
          <div>
            <CustomDropdown
              label={
                formData.tenderFor === "staff"
                  ? "Staff Position"
                  : "Player Position *"
              }
              options={
                formData.tenderFor === "staff"
                  ? staffPositions
                  : playerPositions
              }
              value={formData.position}
              onChange={(value) =>
                setFormData({ ...formData, position: value })
              }
              placeholder={
                formData.tenderFor === "staff"
                  ? "Staff Position"
                  : "Player Position"
              }
              displayProperty="label"
              valueProperty="value"
              searchEnabled={true}
              searchPlaceholder="Search position..."
            />
            {fieldErrors.position && (
              <p className="mt-1 text-sm text-red-500">{fieldErrors.position}</p>
            )}
          </div>

          {formData.tenderFor === "player" && (
            <>
              <div>
                <CustomDropdown
                  label="Foot"
                  options={footChoices}
                  value={formData.foot}
                  onChange={(value) =>
                    setFormData({ ...formData, foot: value })
                  }
                  placeholder="Player foot"
                  displayProperty="label"
                  valueProperty="value"
                />
              </div>

              <div>
                <label className="block text-[16px] font-medium text-[#000000] mb-2">
                  Min. Height (cm)
                </label>
                <input
                  type="number"
                  min="100"
                  max="250"
                  value={formData.minHeight || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, minHeight: e.target.value })
                  }
                  placeholder="Enter minimum height in cm (leave empty for any)"
                  className="w-full px-4 py-3 border border-[#E9E9E9] rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </>
          )}

          {formData.tenderFor === "staff" ? (
            <div>
              <label className="block text-[16px] font-medium text-[#000000] mb-2">
                Experience in years
              </label>
              <CustomDropdown
                value={formData.experience}
                onChange={(value) =>
                  setFormData({ ...formData, experience: value })
                }
                options={experienceChoices}
                placeholder="Select experience"
                className="w-full"
              />
            </div>
          ) : (
            <div className="flex gap-6">
              <div className="w-[50%]">
                <label className="block text-[16px] font-medium text-[#000000] mb-2">
                  Min. Age
                </label>
                <input
                  type="text"
                  value={formData.minAge}
                  onChange={(e) =>
                    setFormData({ ...formData, minAge: e.target.value })
                  }
                  className="w-full px-4 py-2.5 bg-white border border-[#E9E9E9] rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 text-[16px] font-medium"
                  placeholder="Min. Age"
                  style={{ color: formData.minAge ? "#000000" : "#00000099" }}
                />
              </div>
              <div className="w-[50%]">
                <label className="block text-[16px] font-medium text-[#000000] mb-2">
                  Max. Age
                </label>
                <input
                  type="text"
                  value={formData.maxAge}
                  onChange={(e) =>
                    setFormData({ ...formData, maxAge: e.target.value })
                  }
                  className="w-full px-4 py-2.5 bg-white border border-[#E9E9E9] rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 text-[16px] font-medium"
                  placeholder="Max. Age"
                  style={{ color: formData.maxAge ? "#000000" : "#00000099" }}
                />
              </div>
            </div>
          )}
        </div>

        {formData.tenderFor === "player" && (
          <>
            <div>
              <label className="block text-[16px] font-medium text-[#000000] mb-2">
                Player Market Value
              </label>
              {/* Three choices side by side need 314px; the form body is only
                  220px wide at 320 and 275px at 375, so the third option
                  ("Not important") walked off the right edge — measured, and
                  visible in Marin's 320px screenshot. Below `sm` they stack
                  instead. `sm` rather than a px-tuned breakpoint because these
                  labels are translated into 16 languages and several are longer
                  than the English ones; it is also the breakpoint this very file
                  already switches on (the Minimum/Maximum Value pair below uses
                  `grid sm:grid-cols-2`). At 640px and above the row is
                  byte-for-byte the old one. */}
              <div className="flex flex-col gap-3 sm:flex-row sm:gap-6">
                {marketValueTypes.map((option) => (
                  <label key={option.value} className="flex items-center">
                    <input
                      type="radio"
                      checked={formData.marketValueType === option.value}
                      onChange={() =>
                        setFormData({
                          ...formData,
                          marketValueType: option.value,
                        })
                      }
                      className="w-4 h-4 shrink-0 text-blue-500 border-[#E9E9E9]"
                    />
                    <span className="ml-2 min-w-0 break-words text-[16px] font-medium text-[#000000]">
                      {option.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {formData.marketValueType === "not_important" ? null : formData.marketValueType === "market_value_fixed" ? (
              <div>
                <label className="block text-[16px] font-medium text-[#000000] mb-2">
                  Market Value
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={formatAmount(formData.marketValue)}
                    onChange={(e) => handleAmountChange(e, "marketValue")}
                    className="w-full pl-4 pr-16 py-2.5 bg-white border border-[#E9E9E9] rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 text-[16px] font-medium"
                    placeholder="Enter amount (max 999'999'999)"
                    style={{
                      color: formData.marketValue ? "#000000" : "#00000099",
                    }}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary">
                    {formData.currency || "USD"}
                  </span>
                </div>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-[16px] font-medium text-[#000000] mb-2">
                    Minimum Value
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={formatAmount(formData.marketValueRange.min)}
                      onChange={(e) =>
                        handleAmountChange(e, "marketValueRange", true, "min")
                      }
                      className="w-full pl-4 pr-16 py-2.5 bg-white border border-[#E9E9E9] rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 text-[16px] font-medium"
                      placeholder="Min amount (max 999'999'999)"
                      style={{
                        color: formData.marketValueRange.min
                          ? "#000000"
                          : "#00000099",
                      }}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary">
                      {formData.currency || "USD"}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-[16px] font-medium text-[#000000] mb-2">
                    Maximum Value
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={formatAmount(formData.marketValueRange.max)}
                      onChange={(e) =>
                        handleAmountChange(e, "marketValueRange", true, "max")
                      }
                      className="w-full pl-4 pr-16 py-2.5 bg-white border border-[#E9E9E9] rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 text-[16px] font-medium"
                      placeholder="Max amount (max 999'999'999)"
                      style={{
                        color: formData.marketValueRange.max
                          ? "#000000"
                          : "#00000099",
                      }}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary">
                      {formData.currency || "USD"}
                    </span>
                  </div>
                </div>
              </div>
            )}
            {fieldErrors.market_value && (
              <p className="mt-1 text-sm text-red-500">
                {fieldErrors.market_value}
              </p>
            )}

            <div>
              <label className="block text-[16px] font-medium text-[#000000] mb-2">
                Games played in
              </label>
              <div className="flex gap-6 flex-col sm:flex-row">
                {GAMES_PLAYED_CHOICES.map((option) => (
                  <label key={option.value} className="flex items-center">
                    <input
                      type="radio"
                      checked={formData.gamesPlayedIn === option.value}
                      onChange={() =>
                        setFormData({
                          ...formData,
                          gamesPlayedIn: option.value,
                        })
                      }
                      className="w-4 h-4 text-blue-500 border-[#E9E9E9]"
                    />
                    <span className="ml-2 text-[16px] font-medium text-[#000000]">
                      {option.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {(formData.gamesPlayedIn === "last_season" ||
              formData.gamesPlayedIn === "last_half_season") && (
              <div>
                <label className="block text-[16px] font-medium text-[#000000] mb-2">
                  Number of games
                </label>
                <input
                  type="text"
                  value={
                    formData.gamesPlayedIn === "last_season"
                      ? formData.matchesPlayedLastSeason
                      : formData.matchesPlayedLastHalfSeason
                  }
                  onChange={(e) => {
                    if (formData.gamesPlayedIn === "last_season") {
                      setFormData({
                        ...formData,
                        matchesPlayedLastSeason: e.target.value,
                      });
                    } else {
                      setFormData({
                        ...formData,
                        matchesPlayedLastHalfSeason: e.target.value,
                      });
                    }
                  }}
                  className="w-full px-4 py-2.5 bg-white border border-[#E9E9E9] rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 text-[16px] font-medium"
                  placeholder={`No. of games played in ${
                    formData.gamesPlayedIn === "last_season"
                      ? "last season"
                      : "last half season"
                  }`}
                  style={{
                    color: (
                      formData.gamesPlayedIn === "last_season"
                        ? formData.matchesPlayedLastSeason
                        : formData.matchesPlayedLastHalfSeason
                    )
                      ? "#000000"
                      : "#00000099",
                  }}
                />
              </div>
            )}

            <div>
              <label className="block text-[16px] font-medium text-[#000000] mb-2">
                Tolerance Value (%)
              </label>
              <input
                type="number"
                value={formData.toleranceValue || ""}
                onChange={(e) =>
                  setFormData({ ...formData, toleranceValue: e.target.value })
                }
                className="w-full px-4 py-2.5 bg-white border border-[#E9E9E9] rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 text-[16px] font-medium"
                placeholder="Enter tolerance percentage"
                min="0"
                max="49"
                style={{
                  color: formData.toleranceValue ? "#000000" : "#00000099",
                }}
              />
              <p className="text-xs text-gray-500 mt-1">
                Requirements are always enforced when agents pitch. Tolerance widens them — leave empty for strict. Must be less than 50%.
              </p>
            </div>
          </>
        )}

        {formData.tenderFor === "staff" && (
          <>
            <div>
              <label className="block text-[16px] font-medium text-[#000000] mb-2">
                Other Staff Position (if not in list)
              </label>
              <input
                type="text"
                value={formData.staffPosition}
                onChange={(e) =>
                  setFormData({ ...formData, staffPosition: e.target.value })
                }
                className="w-full px-4 py-2.5 bg-white border border-[#E9E9E9] rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 text-[16px] font-medium"
                placeholder="Enter other staff position"
                style={{
                  color: formData.staffPosition ? "#000000" : "#00000099",
                }}
              />
            </div>
            
            <div>
              <label className="block text-[16px] font-medium text-[#000000] mb-2">
                Tolerance Value (%)
              </label>
              <input
                type="number"
                value={formData.toleranceValue || ""}
                onChange={(e) =>
                  setFormData({ ...formData, toleranceValue: e.target.value })
                }
                className="w-full px-4 py-2.5 bg-white border border-[#E9E9E9] rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 text-[16px] font-medium"
                placeholder="Enter tolerance percentage"
                min="0"
                max="49"
                style={{
                  color: formData.toleranceValue ? "#000000" : "#00000099",
                }}
              />
              <p className="text-xs text-gray-500 mt-1">
                Requirements are always enforced when agents pitch. Tolerance widens them — leave empty for strict. Must be less than 50%.
              </p>
            </div>
          </>
        )}

        {formData.tenderFor === "staff" && (
          <>
            <div>
              <label className="block text-[16px] font-medium text-[#000000] mb-2">
                Languages known (Optional)
              </label>
              <div className="space-y-3">
                <CustomDropdown
                  options={LANGUAGE_CHOICES.filter(
                    (lang) => !formData.languageSkills.includes(lang.label)
                  )}
                  value=""
                  onChange={handleLanguageChange}
                  placeholder="Select language"
                  displayProperty="label"
                  valueProperty="label"
                  searchEnabled={true}
                  searchPlaceholder="Search language..."
                />

                {formData.languageSkills.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.languageSkills.map((lang) => (
                      <span
                        key={lang}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-md"
                      >
                        {lang}
                        <button
                          type="button"
                          onClick={() => removeLanguage(lang)}
                          className="text-secondary hover:text-gray-900"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-[16px] font-medium text-[#000000] mb-2">
                Offering additional benefits
              </label>
              <div className="flex gap-6">
                <label className="flex items-center">
                  <input
                    type="radio"
                    checked={formData.offeringAdditionalBenefits}
                    onChange={() =>
                      setFormData({
                        ...formData,
                        offeringAdditionalBenefits: true,
                      })
                    }
                    className="w-4 h-4 text-blue-500 border-[#E9E9E9]"
                  />
                  <span className="ml-2 text-[16px] font-medium text-[#000000]">
                    Yes
                  </span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    checked={!formData.offeringAdditionalBenefits}
                    onChange={() =>
                      setFormData({
                        ...formData,
                        offeringAdditionalBenefits: false,
                      })
                    }
                    className="w-4 h-4 text-blue-500 border-[#E9E9E9]"
                  />
                  <span className="ml-2 text-[16px] font-medium text-[#000000]">
                    No
                  </span>
                </label>
              </div>
            </div>
          </>
        )}

        <div>
          <label className="block text-[16px] font-medium text-[#000000] mb-2">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            className="w-full px-4 py-2.5 bg-white border border-[#E9E9E9] rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 min-h-[100px] text-[16px] font-medium"
            placeholder="Add description"
            style={{ color: formData.description ? "#000000" : "#00000099" }}
          />
        </div>

        <div>
          <label className="block text-[16px] font-medium text-[#000000] mb-2">
            Other requirements (Optional)
          </label>
          <textarea
            value={formData.otherRequirements}
            onChange={(e) =>
              setFormData({ ...formData, otherRequirements: e.target.value })
            }
            className="w-full px-4 py-2.5 bg-white border border-[#E9E9E9] rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 min-h-[100px] text-[16px] font-medium"
            placeholder="Add any other requirements you want from the player"
            style={{
              color: formData.otherRequirements ? "#000000" : "#00000099",
            }}
          />
        </div>

        <div>
          <label className="block text-[16px] font-medium text-[#000000] mb-2">
            Attach Document (Optional)
          </label>
          
          {/* File Input */}
          <div className="space-y-3">
            <input
              type="file"
              id="document-upload"
              accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  // Basic validation
                  const maxSize = 5 * 1024 * 1024; // 5MB
                  if (file.size > maxSize) {
                    alert(tr("File size must be less than 5MB"));
                    e.target.value = "";
                    return;
                  }
                  
                  const allowedTypes = [
                    'application/pdf',
                    'application/msword',
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                    'text/plain',
                    'image/jpeg',
                    'image/jpg',
                    'image/png'
                  ];
                  
                  if (!allowedTypes.includes(file.type)) {
                    alert(tr("Please select a valid file type (PDF, DOC, DOCX, TXT, JPG, PNG)"));
                    e.target.value = "";
                    return;
                  }
                  
                  setFormData({
                    ...formData,
                    documents: [file],
                  });
                }
              }}
              className="hidden"
            />

            {/* Upload Button */}
            {hasNewDocument ? (
              <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <svg
                  className="w-5 h-5 text-gray-500 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  {getFileIcon(formData.documents[0].name)}
                </svg>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {formData.documents[0].name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatFileSize(formData.documents[0].size) || "—"}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleViewDocument(formData.documents[0])}
                    className="inline-flex items-center gap-1 text-blue-500 hover:text-blue-700 px-2 py-1 rounded transition-colors duration-200"
                    title="Preview document"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    <span className="text-sm font-medium">Preview</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setFormData({
                        ...formData,
                        documents: [],
                        documentMarkedForRemoval: formData.documentMarkedForRemoval,
                      });
                      const input = document.getElementById('document-upload');
                      if (input) {
                        input.value = "";
                      }
                    }}
                    className="inline-flex items-center gap-1 text-red-500 hover:text-red-700 px-2 py-1 rounded transition-colors duration-200"
                    title="Remove document"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    <span className="text-sm font-medium">Remove</span>
                  </button>
                </div>
              </div>
            ) : shouldShowExistingDocument ? (
              <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <svg
                  className="w-5 h-5 text-gray-500 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  {getFileIcon(formData.existingDocument.name || formData.existingDocument.url || "document")}
                </svg>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {formData.existingDocument.name}
                  </p>
                  {formatFileSize(formData.existingDocument.size) && (
                    <p className="text-xs text-gray-500">
                      {formatFileSize(formData.existingDocument.size)}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleViewDocument(formData.existingDocument)}
                    className="inline-flex items-center gap-1 text-blue-500 hover:text-blue-700 px-2 py-1 rounded transition-colors duration-200"
                    title="Preview document"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    <span className="text-sm font-medium">Preview</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setFormData({
                        ...formData,
                        existingDocument: null,
                        documentMarkedForRemoval: true,
                      });
                      const input = document.getElementById('document-upload');
                      if (input) {
                        input.value = "";
                      }
                    }}
                    className="inline-flex items-center gap-1 text-red-500 hover:text-red-700 px-2 py-1 rounded transition-colors duration-200"
                    title="Remove document"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    <span className="text-sm font-medium">Remove</span>
                  </button>
                </div>
              </div>
            ) : (
              <label
                htmlFor="document-upload"
                className="bg-white border border-[#E6E6E6] rounded-lg inline-flex items-center gap-2 px-4 py-2 text-[16px] font-medium text-[#000000] hover:bg-gray-50 transition-colors duration-200 cursor-pointer"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                  />
                </svg>
                Attach Document
              </label>
            )}
            
            <p className="text-xs text-gray-500">
              Supported formats: PDF, DOC, DOCX, TXT, JPG, PNG (Max 5MB)
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenderFormRequirements;
